import { motion } from "framer-motion";
import { MapPin, Calendar, Users, Star } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const ActivitiesHeroSection = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState("");

  const handleExplore = () => {
    if (selectedCountry) {
      navigate("/activity", { state: { countryId: selectedCountry } });
    } else {
      navigate("/activity");
    }
  };

  return (
    <section className="relative min-h-[90vh] w-full flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?q=80&w=1234&auto=format&fit=crop')",
        }}
      ></div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/20"></div>

      {/* MAIN CONTENT */}
      <div className="relative z-10 container mx-auto px-4 md:px-10 max-w-6xl">
        {/* Heading Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-6xl font-extrabold text-white drop-shadow-lg leading-tight">
            Discover Amazing Activities <br />
            <span className="text-blue-300 text-4xl">Around the World</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-200 mt-3 max-w-3xl mx-auto leading-relaxed">
            Book unforgettable experiences, tours, and activities. From adventure
            sports to cultural tours, find your perfect activity today.
          </p>
        </motion.div>

        {/* MAIN FORM CARD */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="backdrop-blur-xl bg-white/20 border border-white/20 rounded-3xl shadow-2xl p-8 md:p-10"
        >
          <div className="mb-6">
            <label className="block text-white font-semibold mb-3 text-lg">
              Select Destination Country
            </label>
            <div className="relative">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-6 py-4 bg-white/90 text-gray-900 border-2 border-gray-300 rounded-xl cursor-pointer text-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-300 transition-all"
              >
                <option value="">All Countries</option>
                {/* Options will be populated dynamically */}
              </select>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExplore}
            className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 transition-all 
              text-white font-bold text-lg py-4 px-12 rounded-xl flex items-center justify-center gap-3 mx-auto shadow-xl"
          >
            Explore Activities
            <MapPin className="w-6 h-6" />
          </motion.button>
        </motion.div>

        {/* ADDITIONAL INFO ROW */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-6 text-center"
        >
          <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl shadow-lg">
            <MapPin className="w-10 h-10 text-blue-300 mx-auto mb-3" />
            <h3 className="text-white text-xl font-bold">100+ Destinations</h3>
            <p className="text-gray-200 text-sm mt-1">
              Activities available worldwide.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl shadow-lg">
            <Calendar className="w-10 h-10 text-green-300 mx-auto mb-3" />
            <h3 className="text-white text-xl font-bold">Instant Booking</h3>
            <p className="text-gray-200 text-sm mt-1">
              Book your activity in minutes.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl shadow-lg">
            <Users className="w-10 h-10 text-yellow-300 mx-auto mb-3" />
            <h3 className="text-white text-xl font-bold">Group Friendly</h3>
            <p className="text-gray-200 text-sm mt-1">
              Perfect for families and groups.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl shadow-lg">
            <Star className="w-10 h-10 text-purple-300 mx-auto mb-3" />
            <h3 className="text-white text-xl font-bold">Top Rated</h3>
            <p className="text-gray-200 text-sm mt-1">
              Highly rated by travelers.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ActivitiesHeroSection;
